import { type ReactNode, useContext } from 'react'
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  OverlayArrow,
  TooltipTriggerStateContext,
} from 'react-aria-components'
import cx from 'classnames'
import { m, useReducedMotion, type Variants } from 'motion/react'
import { useModalAnimationStateControls } from '@/contexts/animation-state-context'
import styles from './Tooltip.module.css'
import type { Placement } from 'react-aria'
import { ScreenHeaderTopBarReactAriaPortalProvider } from '@/components/ScreenHeader'
import { isHoverSupported } from '@/utils/browser-util'

const MotionTooltip = m.create(AriaTooltip)

export interface TooltipProps {
  children?: ReactNode
  offset?: number
  placement?: Placement
  testId?: string
}

const TooltipVariantNames = {
  VISIBLE: 'tooltip-visible',
  HIDDEN: 'tooltip-hidden',
}

const tooltipVariants: Variants = {
  [TooltipVariantNames.VISIBLE]: {
    '--animation-offset': '0px',
    opacity: 1,
  },
  [TooltipVariantNames.HIDDEN]: {
    '--animation-offset': '2px',
    opacity: 0,
  },
}

const tooltipVariantsReducedMotion: Variants = {
  [TooltipVariantNames.VISIBLE]: {
    '--animation-offset': '0px',
    opacity: 1,
  },
  [TooltipVariantNames.HIDDEN]: {
    '--animation-offset': '0px',
    opacity: 0,
  },
}

/**
 * The tooltip content itself. Render inside a {@link TooltipTrigger}.
 */
export function Tooltip({
  children,
  placement,
  offset = 6,
  testId,
}: TooltipProps) {
  const shouldReduceMotion = useReducedMotion()
  const { isOpen = false } = useContext(TooltipTriggerStateContext) ?? {}
  const animationStateProps = useModalAnimationStateControls({
    isOpen,
    visibleVariant: TooltipVariantNames.VISIBLE,
    hiddenVariant: TooltipVariantNames.HIDDEN,
  })
  return (
    <ScreenHeaderTopBarReactAriaPortalProvider>
      <MotionTooltip
        data-test={testId}
        className={({ placement }) =>
          cx(styles.root, placement && styles[placement])
        }
        offset={offset}
        placement={placement}
        variants={
          shouldReduceMotion ? tooltipVariantsReducedMotion : tooltipVariants
        }
        transition={{
          duration: 0.1,
        }}
        {...animationStateProps}
      >
        <OverlayArrow className={styles.arrow}>
          <svg
            className={styles.arrowSvg}
            viewBox="0 0 16 16"
            preserveAspectRatio="none"
          >
            <path stroke="none" fill="currentColor" d="M0 0 L8 7 L16 0" />
          </svg>
        </OverlayArrow>
        <div className={styles.content}>{children}</div>
      </MotionTooltip>
    </ScreenHeaderTopBarReactAriaPortalProvider>
  )
}

export interface TooltipTriggerProps {
  children?: ReactNode
  isOpen?: boolean
}

/**
 * Tooltip trigger. Render an interactive element such as a Button, as well
 * as a {@link Tooltip} as children to automatically wire up the tooltip to
 * the interactive element.
 */
export function TooltipTrigger({ isOpen, children }: TooltipTriggerProps) {
  return (
    <AriaTooltipTrigger isOpen={isOpen} isDisabled={!isHoverSupported}>
      {children}
    </AriaTooltipTrigger>
  )
}
